import React, { useState, useMemo } from 'react';
import { Estado, Cidade } from '../../types/sistema';
import { sistemaService } from '../../services/sistemaService';
import {
  MapPin,
  Building,
  Plus,
  Pencil,
  Trash2,
  Search,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { EstadoFormModal } from './EstadoFormModal';
import { CidadeFormModal } from './CidadeFormModal';

interface EstadosCidadesSectionProps {
  estados: Estado[];
  cidades: Cidade[];
  loading?: boolean;
  onRefresh: () => void;
}

export const EstadosCidadesSection: React.FC<EstadosCidadesSectionProps> = ({
  estados,
  cidades,
  loading = false,
  onRefresh,
}) => {
  // Estados Search
  const [estadoSearch, setEstadoSearch] = useState('');
  const [isEstadoModalOpen, setIsEstadoModalOpen] = useState(false);
  const [selectedEstado, setSelectedEstado] = useState<Estado | null>(null);

  // Cidades Search & Filter
  const [cidadeSearch, setCidadeSearch] = useState('');
  const [cidadeEstadoFilter, setCidadeEstadoFilter] = useState<string>('todos');
  const [isCidadeModalOpen, setIsCidadeModalOpen] = useState(false);
  const [selectedCidade, setSelectedCidade] = useState<Cidade | null>(null);

  // Exclusão de Estado
  const [estadoToDelete, setEstadoToDelete] = useState<Estado | null>(null);
  const [isDeletingEstado, setIsDeletingEstado] = useState(false);
  const [deleteEstadoError, setDeleteEstadoError] = useState<string | null>(null);

  // Exclusão de Cidade
  const [cidadeToDelete, setCidadeToDelete] = useState<Cidade | null>(null);
  const [isDeletingCidade, setIsDeletingCidade] = useState(false);
  const [deleteCidadeError, setDeleteCidadeError] = useState<string | null>(null);

  // Mapa de Estado por ID para visualização rápida na lista de cidades
  const estadoMap = useMemo(() => {
    const map = new Map<number, Estado>();
    estados.forEach((est) => map.set(est.id, est));
    return map;
  }, [estados]);

  // Filtro de Estados
  const filteredEstados = useMemo(() => {
    if (!estadoSearch.trim()) return estados;
    const term = estadoSearch.toLowerCase();
    return estados.filter(
      (e) =>
        e.nome.toLowerCase().includes(term) ||
        (e.sigla && e.sigla.toLowerCase().includes(term)) ||
        ((e as any).uf && String((e as any).uf).toLowerCase().includes(term))
    );
  }, [estados, estadoSearch]);

  // Filtro de Cidades
  const filteredCidades = useMemo(() => {
    let result = cidades;
    if (cidadeEstadoFilter !== 'todos') {
      const targetEstadoId = Number(cidadeEstadoFilter);
      result = result.filter((c) => c.estado_id === targetEstadoId);
    }
    if (cidadeSearch.trim()) {
      const term = cidadeSearch.toLowerCase();
      result = result.filter(
        (c) =>
          c.nome.toLowerCase().includes(term) ||
          (c.codigo_ibge && c.codigo_ibge.includes(term)) ||
          (estadoMap.get(c.estado_id)?.sigla?.toLowerCase().includes(term))
      );
    }
    return result;
  }, [cidades, cidadeEstadoFilter, cidadeSearch, estadoMap]);

  // Handlers para Estados
  const handleOpenCreateEstado = () => {
    setSelectedEstado(null);
    setIsEstadoModalOpen(true);
  };

  const handleOpenEditEstado = (estado: Estado) => {
    setSelectedEstado(estado);
    setIsEstadoModalOpen(true);
  };

  const handleConfirmDeleteEstado = async () => {
    if (!estadoToDelete) return;
    setIsDeletingEstado(true);
    setDeleteEstadoError(null);
    try {
      const res = await sistemaService.deleteEstado(estadoToDelete.id);
      if (!res.success) {
        setDeleteEstadoError(res.error || 'Erro ao excluir estado.');
        setIsDeletingEstado(false);
        return;
      }
      setEstadoToDelete(null);
      onRefresh();
    } catch (err: any) {
      console.error('Erro ao excluir estado:', err);
      setDeleteEstadoError(err.message || 'Erro inesperado ao excluir estado.');
    } finally {
      setIsDeletingEstado(false);
    }
  };

  // Handlers para Cidades
  const handleOpenCreateCidade = () => {
    setSelectedCidade(null);
    setIsCidadeModalOpen(true);
  };

  const handleOpenEditCidade = (cidade: Cidade) => {
    setSelectedCidade(cidade);
    setIsCidadeModalOpen(true);
  };

  const handleConfirmDeleteCidade = async () => {
    if (!cidadeToDelete) return;
    setIsDeletingCidade(true);
    setDeleteCidadeError(null);
    try {
      const res = await sistemaService.deleteCidade(cidadeToDelete.id);
      if (!res.success) {
        setDeleteCidadeError(res.error || 'Erro ao excluir cidade.');
        setIsDeletingCidade(false);
        return;
      }
      setCidadeToDelete(null);
      onRefresh();
    } catch (err: any) {
      console.error('Erro ao excluir cidade:', err);
      setDeleteCidadeError(err.message || 'Erro inesperado ao excluir cidade.');
    } finally {
      setIsDeletingCidade(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* CARD 1: ESTADOS CADASTRADOS */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col">
        {/* Header com Título e Botão Novo */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-red-50 text-red-600 rounded-lg">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900">
                Estados Cadastrados ({estados.length})
              </h2>
              <p className="text-[11px] text-slate-400">Unidades Federativas brasileiras</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleOpenCreateEstado}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-2xs hover:shadow-xs transition-all cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Novo Estado</span>
          </button>
        </div>

        {/* Barra de Busca de Estados */}
        <div className="relative mb-3">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={estadoSearch}
            onChange={(e) => setEstadoSearch(e.target.value)}
            placeholder="Filtrar estado ou sigla..."
            className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:bg-white text-slate-800 placeholder-slate-400"
          />
        </div>

        {/* Lista de Estados */}
        <div className="max-h-96 overflow-y-auto space-y-1.5 pr-1 divide-y divide-slate-100 flex-1">
          {loading ? (
            <div className="py-8 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-red-600" />
              <span>Carregando estados...</span>
            </div>
          ) : filteredEstados.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-xs">
              {estadoSearch ? 'Nenhum estado encontrado para a busca.' : 'Nenhum estado cadastrado.'}
            </div>
          ) : (
            filteredEstados.map((est) => (
              <div
                key={est.id}
                className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-xl text-xs transition-colors group pt-2.5"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-slate-800 truncate">{est.nome}</span>
                  {est.codigo_ibge && (
                    <span className="text-[10px] text-slate-400 font-mono hidden sm:inline">
                      IBGE: {est.codigo_ibge}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-700 font-bold text-[11px]">
                    {est.sigla || (est as any).uf}
                  </span>
                  <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => handleOpenEditEstado(est)}
                      title="Editar Estado"
                      className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteEstadoError(null);
                        setEstadoToDelete(est);
                      }}
                      title="Excluir Estado"
                      className="p-1 text-slate-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* CARD 2: CIDADES CADASTRADAS */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col">
        {/* Header com Título e Botão Nova */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-red-50 text-red-600 rounded-lg">
              <Building className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900">
                Cidades Cadastradas ({cidades.length})
              </h2>
              <p className="text-[11px] text-slate-400">Municípios atendidos</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleOpenCreateCidade}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-2xs hover:shadow-xs transition-all cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Nova Cidade</span>
          </button>
        </div>

        {/* Filtros de Busca de Cidades */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={cidadeSearch}
              onChange={(e) => setCidadeSearch(e.target.value)}
              placeholder="Buscar cidade..."
              className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:bg-white text-slate-800 placeholder-slate-400"
            />
          </div>
          <div>
            <select
              value={cidadeEstadoFilter}
              onChange={(e) => setCidadeEstadoFilter(e.target.value)}
              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:bg-white text-slate-700 font-medium"
            >
              <option value="todos">Todos os Estados ({estados.length})</option>
              {estados.map((est) => (
                <option key={est.id} value={est.id}>
                  {est.nome} ({est.sigla || (est as any).uf})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Lista de Cidades */}
        <div className="max-h-96 overflow-y-auto space-y-1.5 pr-1 divide-y divide-slate-100 flex-1">
          {loading ? (
            <div className="py-8 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-red-600" />
              <span>Carregando cidades...</span>
            </div>
          ) : filteredCidades.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-xs">
              {cidadeSearch || cidadeEstadoFilter !== 'todos'
                ? 'Nenhuma cidade encontrada para os filtros aplicados.'
                : 'Nenhuma cidade cadastrada.'}
            </div>
          ) : (
            filteredCidades.map((cid) => {
              const estadoObj = estadoMap.get(cid.estado_id);
              const estadoLabel = estadoObj ? (estadoObj.sigla || estadoObj.nome) : `UF ID: ${cid.estado_id}`;

              return (
                <div
                  key={cid.id}
                  className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-xl text-xs transition-colors group pt-2.5"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold text-slate-800 truncate">{cid.nome}</span>
                    {cid.codigo_ibge && (
                      <span className="text-[10px] text-slate-400 font-mono hidden sm:inline">
                        IBGE: {cid.codigo_ibge}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                      {estadoLabel}
                    </span>
                    <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => handleOpenEditCidade(cid)}
                        title="Editar Cidade"
                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteCidadeError(null);
                          setCidadeToDelete(cid);
                        }}
                        title="Excluir Cidade"
                        className="p-1 text-slate-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* MODAL ESTADO */}
      <EstadoFormModal
        isOpen={isEstadoModalOpen}
        onClose={() => setIsEstadoModalOpen(false)}
        estado={selectedEstado}
        onSuccess={onRefresh}
      />

      {/* MODAL CIDADE */}
      <CidadeFormModal
        isOpen={isCidadeModalOpen}
        onClose={() => setIsCidadeModalOpen(false)}
        cidade={selectedCidade}
        estados={estados}
        defaultEstadoId={cidadeEstadoFilter !== 'todos' ? Number(cidadeEstadoFilter) : undefined}
        onSuccess={onRefresh}
      />

      {/* MODAL CONFIRMAÇÃO EXCLUSÃO ESTADO */}
      {estadoToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-slate-100 p-6 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-amber-600 mb-3">
              <div className="p-2 bg-amber-50 rounded-xl">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-900 text-base">Excluir Estado?</h3>
            </div>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">
              Tem certeza que deseja remover o estado <b>"{estadoToDelete.nome}" ({estadoToDelete.sigla || (estadoToDelete as any).uf})</b>?
            </p>
            {deleteEstadoError && (
              <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs leading-relaxed">
                {deleteEstadoError}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEstadoToDelete(null)}
                disabled={isDeletingEstado}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteEstado}
                disabled={isDeletingEstado}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all disabled:opacity-50 cursor-pointer"
              >
                {isDeletingEstado ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    Sim, Excluir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONFIRMAÇÃO EXCLUSÃO CIDADE */}
      {cidadeToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-slate-100 p-6 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-amber-600 mb-3">
              <div className="p-2 bg-amber-50 rounded-xl">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-900 text-base">Excluir Cidade?</h3>
            </div>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">
              Tem certeza que deseja remover o município <b>"{cidadeToDelete.nome}"</b>?
            </p>
            {deleteCidadeError && (
              <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs leading-relaxed">
                {deleteCidadeError}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setCidadeToDelete(null)}
                disabled={isDeletingCidade}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteCidade}
                disabled={isDeletingCidade}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all disabled:opacity-50 cursor-pointer"
              >
                {isDeletingCidade ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    Sim, Excluir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
